import { Controller } from "@hotwired/stimulus";
import Sortable from "sortablejs";
import { i18n } from "../lib/i18n";

export default class extends Controller {
  static targets = ["metric"];
  static values = {
    dashboardId: Number,
    editable: Boolean
  };

  // Resize state
  resizing = false;
  resizingElement = null;
  startX = 0;
  startY = 0;
  startCols = 0;
  startRows = 0;

  connect() {
    if (this.editableValue) {
      this.initializeSortable();
    }
    this.setupChartResizeObservers();
  }

  disconnect() {
    if (this.sortable) {
      this.sortable.destroy();
    }
    if (this.resizeObservers) {
      this.resizeObservers.forEach((observer) => observer.disconnect());
    }
  }

  initializeSortable() {
    // Whole card is draggable (iOS-style); the overflow menu and resize handle
    // are excluded so they keep their own click/drag behavior.
    this.sortable = new Sortable(this.element, {
      animation: 150,
      draggable: "[data-dashboard-grid-target='metric']",
      filter: ".dropdown, [data-action*='startResize']",
      preventOnFilter: false,
      ghostClass: "sortable-ghost",
      chosenClass: "sortable-chosen",
      onEnd: this.onSortEnd.bind(this)
    });

    // Tooltip on drag handles (cursor is handled via CSS — cursor-grab).
    this.metricTargets.forEach((metric) => {
      const handle = metric.querySelector(".drag-handle");
      if (handle) {
        handle.title = i18n.t('dashboard_grid.drag_tooltip');
      }
    });
  }

  getGridColumnCount() {
    const styles = window.getComputedStyle(this.element);
    const columns = styles.getPropertyValue('grid-template-columns').split(' ').length;
    return columns;
  }

  getCurrentCols(element) {
    // Read the 12-column (@5xl) value as canonical cols
    const lgMatch = element.className.match(/@5xl:col-span-(\d+)/);
    if (lgMatch) return parseInt(lgMatch[1]);
    const mdMatch = element.className.match(/@xl:col-span-(\d+)/);
    return mdMatch ? parseInt(mdMatch[1]) : 3;
  }

  getCurrentRows(element) {
    const match = element.className.match(/row-span-(\d+)/);
    return match ? parseInt(match[1]) : 2;
  }

  // Pointer coordinates from a mouse or touch event (touchend uses changedTouches).
  pointerCoords(event) {
    const point =
      (event.touches && event.touches[0]) ||
      (event.changedTouches && event.changedTouches[0]) ||
      event;
    return { x: point.pageX, y: point.pageY };
  }

  startResize(event) {
    if (!this.editableValue) return;

    event.preventDefault();
    this.resizing = true;
    this.resizingElement = event.target.closest("[data-metric-id]");

    // Get current column span (canonical value from lg: class)
    this.startCols = this.getCurrentCols(this.resizingElement);
    this.startRows = this.getCurrentRows(this.resizingElement);

    const start = this.pointerCoords(event);
    this.startX = start.x;
    this.startY = start.y;

    // Add event listeners (mouse + touch so the handle works on mobile)
    this.boundHandleResize = this.handleResize.bind(this);
    this.boundStopResize = this.stopResize.bind(this);
    document.addEventListener("mousemove", this.boundHandleResize);
    document.addEventListener("mouseup", this.boundStopResize);
    document.addEventListener("touchmove", this.boundHandleResize, { passive: false });
    document.addEventListener("touchend", this.boundStopResize);
    document.addEventListener("touchcancel", this.boundStopResize);

    // Visual feedback
    this.resizingElement.style.opacity = "0.8";
    this.resizingElement.style.transition = "none"; // Disable transitions during resize
    this.resizingElement.classList.add("resizing");
    document.body.style.cursor = "se-resize";

    // Check if at minimum size
    if (this.startRows === this.minRows(this.resizingElement)) {
      this.resizingElement.classList.add("at-min-height");
    }
  }

  handleResize = (event) => {
    if (!this.resizing) return;

    // Stop the page from scrolling while resizing via touch.
    if (event.cancelable) event.preventDefault();

    const { x, y } = this.pointerCoords(event);

    // Calculate column changes
    const gridWidth = this.element.offsetWidth;
    const gridCols = this.getGridColumnCount();
    const colWidth = gridWidth / gridCols;
    const deltaX = x - this.startX;
    const deltaCols = Math.round(deltaX / colWidth);
    // A card can carry its own floor: a widget's display needs a certain amount
    // of room to say anything, and the grip stops there rather than letting the
    // server refuse the drag on release. Report cards carry none and keep the
    // grid's own 2 x 1.
    const minCols = this.minCols(this.resizingElement);
    const newCols = Math.max(minCols, Math.min(12, this.startCols + deltaCols));

    // Calculate row changes based on fixed grid row height (120px + 16px gap)
    const rowHeight = 120 + 16; // row height + gap
    const deltaY = y - this.startY;
    const deltaRows = Math.round(deltaY / rowHeight);
    const minRows = this.minRows(this.resizingElement);
    const newRows = Math.max(minRows, Math.min(6, this.startRows + deltaRows));

    // Only update if dimensions actually changed
    const currentCols = this.getCurrentCols(this.resizingElement);
    const currentRows = this.getCurrentRows(this.resizingElement);

    if (newCols !== currentCols || newRows !== currentRows) {
      // Update preview
      let currentClass = this.resizingElement.className;
      const mdCols = Math.min(newCols, 6);
      currentClass = currentClass.replace(
        /@xl:col-span-\d+/g,
        `@xl:col-span-${mdCols}`,
      );
      currentClass = currentClass.replace(
        /@5xl:col-span-\d+/g,
        `@5xl:col-span-${newCols}`,
      );
      currentClass = currentClass.replace(
        /row-span-\d+/g,
        `row-span-${newRows}`,
      );
      this.resizingElement.className = currentClass;

      // Update minimum size indicator
      if (newRows === minRows) {
        this.resizingElement.classList.add("at-min-height");
      } else {
        this.resizingElement.classList.remove("at-min-height");
      }

      // Trigger immediate chart resize for visual feedback
      this.triggerChartResize(this.resizingElement);
    }
  };

  minCols(element) {
    return Number(element?.dataset?.minCols) || 2;
  }

  minRows(element) {
    return Number(element?.dataset?.minRows) || 1;
  }

  stopResize = (event) => {
    if (!this.resizing) return;

    this.resizing = false;

    // Get final dimensions (canonical value from lg: class)
    const finalCols = this.getCurrentCols(this.resizingElement);
    const finalRows = this.getCurrentRows(this.resizingElement);

    // Save if changed
    if (finalCols !== this.startCols || finalRows !== this.startRows) {
      this.resizeMetric(this.resizingElement, finalCols, finalRows);
    }

    // Trigger final chart resize
    this.triggerChartResize(this.resizingElement);

    // Clean up
    this.resizingElement.style.opacity = "";
    this.resizingElement.style.transition = ""; // Re-enable transitions
    this.resizingElement.classList.remove("resizing", "at-min-height");
    document.body.style.cursor = "";
    document.removeEventListener("mousemove", this.boundHandleResize);
    document.removeEventListener("mouseup", this.boundStopResize);
    document.removeEventListener("touchmove", this.boundHandleResize);
    document.removeEventListener("touchend", this.boundStopResize);
    document.removeEventListener("touchcancel", this.boundStopResize);
    this.resizingElement = null;
  };

  triggerChartResize(element) {
    // Force immediate layout recalculation
    element.offsetHeight; // Force reflow

    // Wait for DOM to update
    requestAnimationFrame(() => {
      this.resizeChartsInElement(element);

      // Also dispatch a window resize event to trigger any other resize handlers
      window.dispatchEvent(new Event("resize"));
    });
  }

  setupChartResizeObservers() {
    this.resizeObservers = [];

    this.metricTargets.forEach((metric) => {
      const observer = new ResizeObserver((entries) => {
        if (this.resizeTimeout) {
          clearTimeout(this.resizeTimeout);
        }

        this.resizeTimeout = setTimeout(() => {
          this.resizeChartsInElement(metric);
        }, 250);
      });

      observer.observe(metric);
      this.resizeObservers.push(observer);
    });
  }

  resizeChartsInElement(element) {
    const chartContainers = element.querySelectorAll("[data-chart-container]");

    chartContainers.forEach((container) => {
      const chartId = container.dataset.chartContainer;
      const chart = window.Chartkick?.charts[chartId];
      if (chart && chart.getChartObject()) {
        const chartInstance = chart.getChartObject();

        // Get the actual container dimensions
        const rect = container.getBoundingClientRect();
        const containerWidth = Math.floor(rect.width);
        const containerHeight = Math.floor(rect.height);

        // Skip if dimensions are too small
        if (containerWidth < 50 || containerHeight < 50) {
          return;
        }

        // Update the canvas size
        const canvas = container.querySelector("canvas");
        if (canvas) {
          // Set canvas dimensions
          canvas.style.width = containerWidth + "px";
          canvas.style.height = containerHeight + "px";

          // Ensure chart options are set correctly
          chartInstance.options.maintainAspectRatio = false;
          chartInstance.options.responsive = true;

          // Trigger chart resize
          chartInstance.resize();
        }
      }
    });
  }

  resizeMetric(element, cols, rows = null) {
    const metricId = element.dataset.metricId;

    // Update classes
    const currentClass = element.className;
    const mdCols = Math.min(cols, 6);
    let newClass = currentClass.replace(
      /@xl:col-span-\d+/g,
      `@xl:col-span-${mdCols}`,
    );
    newClass = newClass.replace(
      /@5xl:col-span-\d+/g,
      `@5xl:col-span-${cols}`,
    );

    if (rows !== null) {
      newClass = newClass.replace(/row-span-\d+/g, `row-span-${rows}`);
    }
    element.className = newClass;

    // Prepare update data
    const updateData = { cols: cols };
    if (rows !== null) {
      updateData.rows = rows;
    }

    // Save to server
    fetch(`/dashboards/${this.dashboardIdValue}/metrics/${metricId}`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')
          .content
      },
      body: JSON.stringify({ metric: updateData })
    })
      .then((response) => {
        if (response.ok) {
          // Trigger chart resize after successful save
          requestAnimationFrame(() => {
            this.triggerChartResize(element);
          });
        }
      })
      .catch((error) => {
        console.error("Error updating metric size:", error);
        // Revert the class change if the server update failed
        element.className = currentClass;
      });
  }

  async onSortEnd(event) {
    if (event.oldIndex === event.newIndex) return;

    const metricIds = Array.from(this.element.children)
      .map((el) => el.dataset.metricId)
      .filter((id) => id);

    try {
      const response = await fetch(
        `/dashboards/${this.dashboardIdValue}/metrics/reorder_all`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')
              .content
          },
          body: JSON.stringify({ metric_ids: metricIds })
        },
      );

      if (!response.ok) {
        throw new Error("Failed to reorder metrics");
      }
    } catch (error) {
      console.error("Error reordering metrics:", error);
    }
  }

  metricTargetConnected(element) {
    if (this.editableValue) {
      const title = element.querySelector(".card-title");
      if (title) {
        // Tooltip only — cursor is handled via CSS (cursor-grab on the card).
        title.title = i18n.t('dashboard_grid.drag_tooltip');
      }
      // Resize handles are always visible in edit mode — no show/hide logic here.
    }

    // Setup resize observer for new metric
    const observer = new ResizeObserver((entries) => {
      if (this.resizeTimeout) {
        clearTimeout(this.resizeTimeout);
      }

      this.resizeTimeout = setTimeout(() => {
        this.resizeChartsInElement(element);
      }, 250);
    });

    observer.observe(element);
    if (!this.resizeObservers) {
      this.resizeObservers = [];
    }
    this.resizeObservers.push(observer);
  }
}
